import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Progress } from "./ui/progress";
import { Download, Globe, FileSpreadsheet, Phone, Trophy, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyImportsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/dashboard/my-imports-this-week`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const {
    total_imported = 0,
    contacted = 0,
    contacted_pct = 0,
    converted = 0,
    converted_pct = 0,
    by_source = { google_places: 0, csv_import: 0 },
    window_days = 7,
  } = data;

  return (
    <Card data-testid="my-imports-widget" className="border-emerald-100">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Download className="w-4 h-4 text-emerald-600" />
              My Imports This Week
            </CardTitle>
            <CardDescription>
              Leads you pulled in the last {window_days} days &mdash; and how they're progressing.
            </CardDescription>
          </div>
          <Link
            to="/leads"
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 shrink-0"
            data-testid="my-imports-view-leads"
          >
            View leads <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>

      <CardContent>
        {total_imported === 0 ? (
          <div className="text-center py-6 text-sm text-slate-500" data-testid="my-imports-empty">
            No imports this week.{" "}
            <Link to="/business-search" className="text-emerald-600 hover:underline">
              Find businesses
            </Link>{" "}
            or{" "}
            <Link to="/import" className="text-emerald-600 hover:underline">
              upload a CSV
            </Link>{" "}
            to get started.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Top numbers */}
            <div className="grid grid-cols-3 gap-4">
              <Metric
                label="Imported"
                value={total_imported}
                accent="text-emerald-700"
                testId="my-imports-total"
              />
              <Metric
                label="Contacted"
                value={contacted}
                suffix={`${contacted_pct}%`}
                accent="text-blue-700"
                testId="my-imports-contacted"
              />
              <Metric
                label="Won"
                value={converted}
                suffix={`${converted_pct}%`}
                accent="text-amber-700"
                testId="my-imports-converted"
              />
            </div>

            {/* Progress bars */}
            <div className="space-y-2">
              <ProgressRow
                icon={Phone}
                label="Contacted rate"
                pct={contacted_pct}
                count={contacted}
                total={total_imported}
                color="bg-blue-500"
              />
              <ProgressRow
                icon={Trophy}
                label="Win rate"
                pct={converted_pct}
                count={converted}
                total={total_imported}
                color="bg-amber-500"
              />
            </div>

            {/* Source breakdown */}
            <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t">
              <SourceTag icon={Globe} label="Business Search" count={by_source.google_places || 0} />
              <SourceTag icon={FileSpreadsheet} label="CSV Import" count={by_source.csv_import || 0} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, suffix, accent, testId }) {
  return (
    <div data-testid={testId}>
      <div className={`text-2xl font-semibold ${accent}`}>{value}</div>
      <div className="text-xs text-slate-500 flex items-baseline gap-1">
        <span>{label}</span>
        {suffix && <span className="text-slate-400">· {suffix}</span>}
      </div>
    </div>
  );
}

function ProgressRow({ icon: Icon, label, pct, count, total, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="flex items-center gap-1.5 text-slate-600">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </span>
        <span className="text-slate-500">
          {count}/{total}
        </span>
      </div>
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function SourceTag({ icon: Icon, label, count }) {
  return (
    <span className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" />
      {label}: <span className="font-medium text-slate-700">{count}</span>
    </span>
  );
}
